import { compact, difference, uniq } from 'lodash';

import { AugmentedUpdateEntityInput, EntityStage } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import {
  EntityLinkFormEntity,
  EntityLinkRow,
} from './components/EntityLinkTable.types';
import { DirectHoldingsLinkPane_EntityFragment } from './graphql/DirectHoldingsLinkPane.generated';
import { TrustsBusinessesLinkPane_EntityFragment } from './graphql/TrustsBusinessesLinkPane.generated';

type LinkableEntity =
  | DirectHoldingsLinkPane_EntityFragment
  | TrustsBusinessesLinkPane_EntityFragment;

/** A `Principal` as both link-pane fragments select it. */
interface PrincipalLike {
  individual?: { displayName: string } | null;
}

/**
 * The person-bearing fields across every subtype the link panes query. Each
 * subtype has at most one of them: accounts carry owners, most trusts carry
 * grantors, and charitable vehicles carry donors.
 */
const PRINCIPAL_FIELDS = [
  'owner',
  'owners',
  'grantor',
  'grantors',
  'donors',
] as const;

function isPrincipalLike(value: unknown): value is PrincipalLike {
  return typeof value === 'object' && value !== null && 'individual' in value;
}

/**
 * Pull the display names of whoever owns, granted or donated to this entity.
 *
 * Reading the union's principal fields by name rather than narrowing on
 * `__typename` keeps this to one pass instead of a type guard per subtype;
 * the fields are all optional in the generated types, so a subtype that has
 * none simply yields an empty list.
 */
export function getPrincipalDisplayNames(subtype: object): string[] {
  const principals = PRINCIPAL_FIELDS.flatMap((field) => {
    const value = (subtype as Record<string, unknown>)[field];
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  });

  return compact(
    principals.map((principal) =>
      isPrincipalLike(principal) ? principal.individual?.displayName : null
    )
  );
}

/** An `OwnershipStake` as the Trusts & Businesses fragment selects it. */
interface OwnershipStakeLike {
  owningIndividual?: { displayName: string } | null;
  owningEntity?: { subtype: { displayName: string } } | null;
}

/**
 * Display names of whoever holds a stake in this entity.
 *
 * Businesses carry no `Principal` at all — a `Principal` is always an
 * individual, and business ownership is modelled as stakes, each held by
 * either a person or another entity. Both kinds are named here, so a business
 * owned by a holding company reads as that company rather than as a blank.
 */
export function getOwnershipStakeDisplayNames(
  stakes: OwnershipStakeLike[]
): string[] {
  return uniq(
    compact(
      stakes.map(
        (stake) =>
          stake.owningIndividual?.displayName ??
          stake.owningEntity?.subtype.displayName
      )
    )
  );
}

/** Normalizes either link pane's query result into the shared row shape. */
export function toEntityLinkRow(entity: LinkableEntity): EntityLinkRow {
  const principalNames = getPrincipalDisplayNames(entity.subtype);

  // Only the Trusts & Businesses query selects stakes, and only entities with
  // no principals of their own need them.
  const stakes =
    !principalNames.length && 'ownedByStakes' in entity
      ? (entity.ownedByStakes ?? [])
      : [];

  return {
    id: entity.id,
    displayName: entity.subtype.displayName,
    formattedValue: formatCurrencyNoDecimals(entity.subtype.currentValue),
    ownerNames: principalNames.length
      ? principalNames
      : getOwnershipStakeDisplayNames(stakes),
    typeLabel: entity.extendedDisplayKind,
    integrationEntityIds: (entity.integrationEntities ?? []).map(
      (integrationEntity) => integrationEntity.id
    ),
  };
}

/** The committed baseline, shaped as RHF reports it in `defaultValues`. */
export type EntityLinkBaseline = Record<
  string,
  { integrationEntities?: (string | undefined)[] } | undefined
>;

/**
 * Diffs the form's committed baseline — its `defaultValues`, set when the rows
 * were seeded through `reset` — against its current state, so a save sends
 * add/remove lists rather than replacing each entity's whole set.
 *
 * A row with no baseline entry is one the form gained after seeding, so
 * everything on it counts as an addition.
 */
export function generateEntityUpdates(
  currentEntities: Record<string, EntityLinkFormEntity>,
  baseline: EntityLinkBaseline | undefined
): AugmentedUpdateEntityInput[] {
  return compact(
    Object.entries(currentEntities).map(([entityId, currentData]) => {
      const initial = compact(baseline?.[entityId]?.integrationEntities);
      const toAdd = difference(currentData.integrationEntities, initial);
      const toRemove = difference(initial, currentData.integrationEntities);

      if (!toAdd.length && !toRemove.length) return null;

      const update: AugmentedUpdateEntityInput['update'] = {};
      if (toAdd.length) {
        update.addIntegrationEntityIDs = toAdd;
        // Linking data to an entity brings it out of draft.
        update.stage = EntityStage.Active;
      }
      if (toRemove.length) {
        update.removeIntegrationEntityIDs = toRemove;
      }

      return { id: entityId, update };
    })
  );
}
